import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeftRight, BarChart3, Compass, GitCompareArrows, Wallet } from 'lucide-react'

import { buildExplorePath } from '@/config/explore'
import { BridgeSiteLink } from '@/components/BridgeSiteLink'
import { MarketDataEmpty } from '@/data/marketApi/components/MarketDataStates'
import { useTranslation } from '@/features/locale/LocaleProvider'
import { PairInstrumentLogos } from '@/features/market/PairInstrumentLogos'
import { useNutrimaticMarket } from '@/features/market/NutrimaticMarketProvider'
import { buildPoolHrefFromListing } from '@/features/market/nutrimaticCta'
import { formatPoolRowMetrics } from '@/features/market/nutrimaticFormat'
import type { NutrimaticPoolRow } from '@/features/market/useNutrimaticMarketHub'
import { useAppWeb3 } from '@/hooks'
import { cn } from '@/lib/utils'
import {
  MobileAppShell,
  MobileCard,
  MobileHomeHero,
  MobileSection,
  MobileTokenSearchButton,
} from '@/mobile'
import { useMobileTokenSearch } from '@/mobile/token-picker'
import { usePortfolioAssets } from '@/pages/Portfolio/usePortfolioData'
import { shortenAddress } from '@/utils'
import { WalletConnectControl } from '@/wallet/WalletConnectControl'

function TrendingPairCard({ item }: { item: NutrimaticPoolRow }) {
  const t = useTranslation()
  const href = buildPoolHrefFromListing(item.listing, item.poolAddress)
  const metrics = formatPoolRowMetrics(item.view)
  const positive = metrics.change24h.startsWith('+')
  const label =
    item.listing.pairLabel || `${item.base?.symbol ?? '?'}/${item.quote?.symbol ?? '?'}`

  return (
    <Link
      to={href}
      className="mobile-app__card flex w-40 shrink-0 snap-start flex-col gap-3 p-3.5 active:opacity-90"
    >
      <div className="flex flex-col items-center gap-2 pt-1">
        <PairInstrumentLogos base={item.base} quote={item.quote} size={36} layout="stacked" />
        <div className="w-full truncate text-center text-sm font-medium">{label}</div>
      </div>
      <div className="font-mono text-center text-xl tabular-nums">{metrics.price}</div>
      <div className="mt-auto flex items-end justify-between text-xs">
        <span className={cn(positive ? 'text-profit' : 'text-loss')}>{metrics.change24h}</span>
        <span className="text-muted-foreground">{t.common.volPrefix(metrics.volume24h)}</span>
      </div>
    </Link>
  )
}

export default function MobileHomePage() {
  const t = useTranslation()
  const { account } = useAppWeb3()
  const { assets, nativeBalance } = usePortfolioAssets()
  const { openTokenSearch } = useMobileTokenSearch()
  const { poolRows } = useNutrimaticMarket()
  const trendingPairs = poolRows.slice(0, 6)

  const quickActions = useMemo(
    () => [
      { label: t.homeDashboard.quickAction.swap, href: '/swap', icon: ArrowLeftRight, external: false as const },
      { label: t.homeDashboard.mobile.quickAction.explore, href: buildExplorePath(), icon: Compass, external: false as const },
      { label: t.homeDashboard.mobile.quickAction.markets, href: buildExplorePath('markets'), icon: BarChart3, external: false as const },
      { label: t.homeDashboard.mobile.quickAction.portfolio, href: '/portfolio', icon: Wallet, external: false as const },
      { label: t.homeDashboard.mobile.quickAction.bridge, href: '', icon: GitCompareArrows, external: true as const },
    ],
    [t],
  )

  const balanceLabel = account
    ? nativeBalance
      ? `${nativeBalance.toSignificant(4)} ${nativeBalance.currency.symbol ?? ''}`.trim()
      : assets.length > 0
        ? t.common.assetCount(assets.length)
        : '0'
    : '—'

  return (
    <MobileAppShell className="pb-2">
      <MobileHomeHero />

      <MobileTokenSearchButton onClick={openTokenSearch} />

      <MobileCard className="p-4">
        {account ? (
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">{t.portfolio.walletBalance}</p>
            <p className="mobile-app__hero-metric">{balanceLabel}</p>
            <p className="text-xs text-muted-foreground">{shortenAddress(account)}</p>
          </div>
        ) : (
          <div className="space-y-3 text-center">
            <p className="mobile-app__body-text text-muted-foreground">{t.wallet.connectToSeeBalances}</p>
            <WalletConnectControl variant="cta" />
          </div>
        )}
      </MobileCard>

      <div className="grid grid-cols-2 gap-3">
        {quickActions.map((action) => {
          const Icon = action.icon
          const className = 'mobile-app__card flex min-h-[5.5rem] flex-col items-start justify-between p-4 active:opacity-90'
          const content = (
            <>
              <Icon className="size-5 text-primary" aria-hidden />
              <span className="mobile-app__body-text font-semibold">{action.label}</span>
            </>
          )
          if (action.external) {
            return (
              <BridgeSiteLink key={action.label} className={className}>
                {content}
              </BridgeSiteLink>
            )
          }
          return (
            <Link key={action.label} to={action.href} className={className}>
              {content}
            </Link>
          )
        })}
      </div>

      <MobileSection title={t.homeDashboard.mobile.trendingPairs}>
        <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-1 snap-x snap-mandatory">
          {!trendingPairs.length ? (
            <MarketDataEmpty className="px-1" />
          ) : (
            trendingPairs.map((item) => (
              <TrendingPairCard key={item.listing.venueId} item={item} />
            ))
          )}
        </div>
      </MobileSection>
    </MobileAppShell>
  )
}
